import { CamelCasedProperties } from 'type-fest';
import { z } from 'zod/v4';
import {
  createDefinitionSchema,
  createThresholdOptionsSchema,
  stepOptionsSchema,
  unitOptionsSchema,
} from '../../utils';

/**
 * Type for the spec-driven `DashboardPanel` variant of the node graph.
 */
export interface NodeGraphSpec extends z.TypeOf<typeof nodeGraphSpecSchema> {}

export const nodeGraphThresholdOptionsSchema = createThresholdOptionsSchema(stepOptionsSchema);
export interface NodeGraphStepOption extends z.TypeOf<typeof stepOptionsSchema> {}
export interface NodeGraphThresholdOptions extends z.TypeOf<typeof nodeGraphThresholdOptionsSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the node graph.
 *
 * Mirrors the subset of `StatChart`'s spec that the renderer actually
 * consumes (unit + thresholds), plus optional grouping-label assignments
 * that override edge direction.
 *
 * When `from_label` / `to_label` are unset, the renderer falls back to
 * alphabetical order on the two grouping labels (which is how Prometheus
 * serializes them, via Go `encoding/json`). That default is correct for
 * label pairs whose source name sorts before the target (e.g.
 * `parent_service` / `service`); for pairs like `target` / `source` the
 * user sets these fields explicitly to flip the assignment.
 *
 * Uses `createThresholdOptionsSchema` (not the bare `thresholdOptionsSchema`)
 * because that's the shape the `ThresholdsEditor` writes — it includes
 * `base.color`, which the bare schema strips.
 */
export const nodeGraphSpecSchema = z.object({
  unit: unitOptionsSchema.optional(),
  thresholds: nodeGraphThresholdOptionsSchema.optional(),
  from_label: z.string().optional(),
  to_label: z.string().optional(),
});

/**
 * The schema for a NodeGraph `DashboardPanel` definition.
 */
export interface NodeGraphDefinition extends z.TypeOf<typeof nodeGraphDefinitionSchema> {}
export const nodeGraphDefinitionSchema = createDefinitionSchema('NodeGraph', nodeGraphSpecSchema);

/**
 * Version of the Zod schema used as the base for the props-driven `Viz` variant.
 *
 * Currently identical to the spec-driven variant; kept as a separate alias so
 * we can introduce shape transforms (flattening, etc.) without touching the
 * spec schema, matching the convention used by `StatChart` and `PieChart`.
 */
export const nodeGraphVizSchema = nodeGraphSpecSchema;

type NodeGraphVizSchemaType = z.TypeOf<typeof nodeGraphVizSchema>;

/**
 * Base type for the props-driven `Viz` variant of the node graph.
 *
 * Snake-case keys aren't deep-camel-cased because `unit` and `thresholds`
 * carry their own snake_case fields (e.g. `decimal_places`, `default_color`)
 * that are part of the public spec contract; preserving them avoids forcing
 * a translation layer at every call-site.
 */
export interface NodeGraphPropsBase extends CamelCasedProperties<NodeGraphVizSchemaType> {}
